import type { InstancePolicy, SettingInput } from "@esi/shared";
import { useQueryClient } from "@tanstack/react-query";
import { Lock, LockKeyhole, PenLine, SquareDashed } from "lucide-react";
import { type RefObject, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/Notice";
import { Page } from "@/components/Page";
import { Skeleton } from "@/components/ui/skeleton";
import { Editor } from "../admin/SettingsAdmin.tsx";
import { api } from "../api.ts";
import { ok } from "../api-errors.ts";
import { formatDateTime } from "../i18n/format.ts";
import { t as translate } from "../i18n/i18n.ts";
import { consolePoliciesPath, navigate, SETTINGS_GROUPS, type SettingsGroup } from "../router.tsx";
import { SettingsGroupTabs } from "../settings-shell/SettingsGroupTabs.tsx";
import { type SearchEntry, SettingsSearch } from "../settings-shell/SettingsSearch.tsx";
import {
  SettingsGroupPage,
  SettingsSectionBlock,
  SettingsShell,
} from "../settings-shell/SettingsShell.tsx";
import { SummaryLines } from "../settings-shell/SummaryLines.tsx";
import { hashTarget, settingHash, useGroupFocus } from "../settings-shell/setting-anchors.ts";
import { TypedSettingRow } from "../settings-shell/TypedSettingRow.tsx";
import { useFirstGroupRedirect } from "../settings-shell/use-first-group-redirect.ts";
import type { SettingAction } from "../settings-shell/use-setting-save.ts";
import { useCsrfHeaders } from "./operator.tsx";
import { consoleRefusal } from "./parts.tsx";
import {
  asEntry,
  describePolicy,
  isLockedForTenants,
  isSetForInstance,
  policyProvenance,
  policyText,
} from "./policy-copy.ts";
import {
  OWN_PAGE_POLICIES,
  type PolicySectionId,
  policyGroupOf,
  policyGroups,
  policySectionOf,
  policySectionsOf,
} from "./policy-groups.ts";
import { consoleKeys, usePolicies } from "./queries.ts";

/** A policy's page with its anchor. */
const policyPath = (key: string) => `${consolePoliciesPath(policyGroupOf(key))}${settingHash(key)}`;

const sectionLabel = (id: PolicySectionId) => translate(`adminSettings.sections.${id}`);

/**
 * Instance policies (spec `tenant-administration` "Instance policies"): a value and a lock for
 * every tenant at once, within what the deployment allows, on one page per settings group
 * (`/policies/<group>`), each opening with a summary of what the instance sets, locks
 * and has fixed by the deployment. Without a group, the first one replaces the URL (a link to
 * one policy opens its page).
 */
export function PoliciesPage({ group }: { group?: SettingsGroup }) {
  useFirstGroupRedirect({
    group: group ?? null,
    first: SETTINGS_GROUPS[0],
    groupOfKey: policyGroupOf,
    pathOf: consolePoliciesPath,
  });
  return group ? <PolicyPages group={group} /> : null;
}

function PolicyPages({ group }: { group: SettingsGroup }) {
  const { t, i18n } = useTranslation();
  const policies = usePolicies();
  const data = useMemo(
    () => policies.data?.filter((p) => !OWN_PAGE_POLICIES.includes(p.key)),
    [policies.data],
  );
  const heading = useRef<HTMLHeadingElement>(null);
  const pick = useGroupFocus({
    group,
    settings: data,
    groupOf: policyGroupOf,
    pathOf: policyPath,
    heading,
  });
  // Worded once per list and language, not on every keystroke.
  const { language } = i18n;
  // biome-ignore lint/correctness/useExhaustiveDependencies: the words follow the language.
  const entries = useMemo<SearchEntry[]>(
    () =>
      (data ?? []).map((p) => ({
        key: p.key,
        ...policyText(p.key),
        group: translate(`adminSettings.groups.${policyGroupOf(p.key)}.label`),
        section: sectionLabel(policySectionOf(p.key)),
        value: describePolicy(p, p.effectiveValue),
      })),
    [data, language],
  );
  // The pages holding a policy and how many each sets for the instance, once per list.
  const { groups, counts } = useMemo(() => {
    const list = data ?? [];
    const counts = new Map<SettingsGroup, number>();
    for (const p of list) {
      if (isSetForInstance(p)) {
        const g = policyGroupOf(p.key);
        counts.set(g, (counts.get(g) ?? 0) + 1);
      }
    }
    return { groups: policyGroups(list), counts };
  }, [data]);
  // A page with no policy (an old link) opens the first page that has one.
  const first = groups[0];
  const empty = data !== undefined && first !== undefined && !groups.includes(group);
  useEffect(() => {
    if (empty && first) {
      // An anchor names the policy's own page (and keeps the anchor for the focus).
      const key = hashTarget();
      const target = key && groups.includes(policyGroupOf(key)) ? policyGroupOf(key) : first;
      navigate(`${consolePoliciesPath(target)}${key ? window.location.hash : ""}`, {
        replace: true,
      });
    }
  }, [empty, first, groups]);
  return (
    <Page>
      <SettingsShell
        title={t("console.policies.title")}
        lead={t("console.policies.lead")}
        // The backoffice's two-column row, without a level picker: half width when roomy.
        controlsFrom="lg"
        controls={
          <SettingsSearch
            entries={entries}
            onPick={pick}
            label={t("console.policies.search.label")}
            placeholder={t("console.policies.search.placeholder")}
          />
        }
        // The tabs wait for the list: only pages holding a policy are shown.
        tabs={
          groups.length > 0 ? (
            <SettingsGroupTabs
              label={t("console.policies.navLabel")}
              groups={groups}
              current={group}
              href={consolePoliciesPath}
              groupLabel={(g) => t(`adminSettings.groups.${g}.label`)}
              count={(g) => counts.get(g) ?? 0}
              countLabel={(_, count) => t("console.policies.setCount", { count })}
            />
          ) : null
        }
      >
        {policies.isError ? (
          <Notice tone="danger">{policies.error.message}</Notice>
        ) : !data || empty ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <PolicyGroup
            key={group}
            group={group}
            policies={data.filter((p) => policyGroupOf(p.key) === group)}
            headingRef={heading}
            onReveal={pick}
          />
        )}
      </SettingsShell>
    </Page>
  );
}

/** One policies page: its heading and purpose, the summary, then its sections of rows. */
function PolicyGroup(props: {
  group: SettingsGroup;
  policies: readonly InstancePolicy[];
  headingRef: RefObject<HTMLHeadingElement | null>;
  onReveal: (key: string) => void;
}) {
  const { group, policies } = props;
  const { t } = useTranslation();
  const items = (list: readonly InstancePolicy[]) =>
    list.map((p) => ({
      key: p.key,
      label: policyText(p.key).label,
      value: describePolicy(p, p.effectiveValue),
    }));
  return (
    <SettingsGroupPage
      id={group}
      heading={t(`adminSettings.groups.${group}.label`)}
      headingRef={props.headingRef}
      purpose={t(`console.policies.groups.${group}`)}
      summary={
        <SummaryLines
          lines={[
            {
              id: "setHere",
              icon: PenLine,
              lead: t("console.policies.summary.setHere"),
              items: items(policies.filter(isSetForInstance)),
            },
            {
              id: "locked",
              icon: LockKeyhole,
              lead: t("console.policies.summary.locked"),
              items: items(policies.filter(isLockedForTenants)),
            },
            {
              id: "fixed",
              icon: Lock,
              lead: t("console.policies.summary.fixed"),
              items: items(policies.filter((p) => p.deploymentLock !== null)),
            },
          ]}
          empty={{
            icon: SquareDashed,
            text: t("console.policies.summary.allDefault", { count: policies.length }),
          }}
          onReveal={props.onReveal}
        />
      }
    >
      {policySectionsOf(group, policies).map((section) => (
        <SettingsSectionBlock key={section.id} id={section.id} heading={sectionLabel(section.id)}>
          {section.settings.map((p) => (
            <PolicyRow
              // Kept across refetches: an open editor keeps its draft.
              key={p.key}
              policy={p}
              policies={policies}
            />
          ))}
        </SettingsSectionBlock>
      ))}
    </SettingsGroupPage>
  );
}

function PolicyRow(props: { policy: InstancePolicy; policies: readonly InstancePolicy[] }) {
  const { policy } = props;
  const { t } = useTranslation();
  const headers = useCsrfHeaders();
  const queryClient = useQueryClient();
  const { label, help } = policyText(policy.key);
  const entry = asEntry(policy);
  const settle = () => queryClient.invalidateQueries({ queryKey: consoleKeys.policies });
  const param = { key: policy.key };
  const run = async (action: SettingAction) => {
    const result =
      action.type === "put"
        ? ok(
            await api.api.operator.policies[":key"].$put(
              { param, json: { value: action.value, locked: action.locked } },
              headers,
            ),
          )
        : ok(await api.api.operator.policies[":key"].$delete({ param }, headers));
    return await result;
  };
  const own = Object.hasOwn(policy, "value");
  const editable = policy.deploymentLock === null;
  // Efforts outside the allowed ones cannot be the default.
  const allowed = props.policies.find((p) => p.key === "models.allowed")?.effectiveValue;
  const disabledOptions =
    policy.key === "models.defaultEffort" && Array.isArray(allowed)
      ? ((policy.input as { options?: string[] }).options ?? []).filter((r) => !allowed.includes(r))
      : undefined;
  return (
    <TypedSettingRow
      settingKey={policy.key}
      input={policy.input as SettingInput}
      label={label}
      help={help || undefined}
      value={policy.effectiveValue}
      locked={policy.locked}
      editable={editable}
      lockable={editable}
      lockCopy="lockEveryTenant"
      resettable={own}
      resetText={t("console.policies.reset")}
      setHere={isSetForInstance(policy)}
      lockedAbove={policy.deploymentLock !== null}
      marks={policyProvenance(policy)}
      extra={
        policy.deploymentLock ? (
          <span className="inline-block first-letter:uppercase">{policy.deploymentLock}</span>
        ) : policy.updatedAt && own ? (
          <span>{t("console.policies.changedAt", { when: formatDateTime(policy.updatedAt) })}</span>
        ) : null
      }
      describe={(value) => describePolicy(policy, value)}
      optionLabel={
        policy.key === "models.allowed"
          ? (o) => describePolicy(policy, [o])
          : (o) => describePolicy(policy, o)
      }
      disabledOptions={disabledOptions}
      run={run}
      settle={settle}
      refusal={consoleRefusal}
      // Lists and structured values (guidelines, placement, domains); `tools.egress` has its lines.
      editor={({ id, value, onChange }) => (
        <Editor id={id} setting={entry} scheme={[]} value={value} onChange={onChange} />
      )}
    />
  );
}
